import SuperJSON from 'superjson';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  AgentMode,
  AgentStreamError,
  agentStreamError,
  AgentStreamErrorKind,
  ClaudeMessage,
  Conversation,
  TOOL_BREAK_MESSAGE,
  TRUNCATED_OUTPUT_MESSAGE,
  TURN_TIMEOUT_MESSAGE,
} from '~api';
import { AgentManager, AgentManagerConfig } from '~portal/agents';
import { getAppEnv } from '~portal/env';
import { logger } from '~portal/lib';

export type ThinkingTrace = {
  // summarised reasoning text emitted before this model call's narration/tools
  text: string;
  // approximate thinking duration (ms) — time-to-first-text, since text only
  // begins after thinking completes
  tElapsed?: number;
};

export type NarrationGroup = {
  // interleaved reasoning that preceded this model call, if any
  thinking?: ThinkingTrace;
  narration: {
    text: string;
    timestamp: Date;
  };
  operations: Array<{
    call: ClaudeMessage.C.ToolUse;
    result: null | Conversation.ToolOutputEvent<Date, string>;
    /**
     * @desc ISO start of the model call that authored this tool call, carried from the
     * `ClaudeMessage` the tool_use arrived on. The step the user watches begins here — the
     * model writes the script for seconds before the sandbox runs it for milliseconds — so
     * the operation line reports `tFinish - stepStart` and keeps the execution time as
     * secondary detail. Undefined on conversations recorded before modelCall metadata
     * existed; the line then falls back to execution time alone.
     */
    stepStart: string | undefined;
  }>;
};

export type ClientEventGroup = {
  turnId: string;
  tStart?: string; // todo: change to Date in payload
  // user prompt that started this turn
  prompt: string;
  // chronological timeline of narrations with their associated operations
  timeline: NarrationGroup[];
  // the final reply from the agent (text block without tool calls)
  response: null | {
    text: string;
    timestamp: Date;
    /**
     * @desc The history event id of the model message — what a thumbs verdict is keyed on
     * (H2-205). Undefined on events recorded before identity existed; no feedback can be given
     * on those, so the thumbs stay hidden.
     */
    messageId: string | undefined;
  };
  // whether the agent is currently thinking (waiting for LLM response)
  isThinking: boolean;
  // whether the agent is warming up (ClickHouse having consecutive timeouts)
  isWarmingUp: boolean;
  /**
   * @desc Why the turn ended without an answer.
   *
   * Two sources, deliberately: a failure the conversation RECORDED (`ModelCallFailed`) is
   * reduced from the history event, so live and reload render identically and a reload cannot
   * lose it; a failure with no history event — a refused prompt, a dropped connection, a write
   * that never landed — arrives on the run-error side channel and is set directly. Anything the
   * backend persists must come down the first path: the second races the turn's own
   * `TurnComplete`, whose arrival releases the socket.
   */
  error: null | AgentStreamError;
  /**
   * @desc A recorded `ModelCallFailed` the backend classed `kind: 'system-failure'` (a provider or
   * transport fault, Exxo's to fix): its generic `message` and the `reference` a user quotes — the
   * event's own (the turn id, task 5.9), else the provider's `requestId` on events recorded before
   * it — for surfaces that name the fault (the automation chat, D13 of
   * add-ario-identity-and-authoring-round-3). Never the provider's own text, which stays in the
   * event's `error`. The product chat ignores it: its `error` presentation is unchanged.
   */
  systemFailure?: { message: string; reference: string | null } | null;
  // Set on the one turn where the conversation crossed the context threshold (H2-152). The backend
  // mints at most one of these per conversation, so this is a property of a single group rather
  // than of the conversation, and it stays wherever it was raised as the chat scrolls on.
  lengthAdvisory: boolean;
};

/**
 * @desc Every tool-output item of one turn, in event order — the input to `latestRevisions`.
 *
 * The turn's own shape (narration groups, each with its operations) is a client construct, so
 * the flatten lives beside it rather than in the fold: `latestRevisions` folds items whatever
 * produced them, and every "files for this turn" surface reaches it through this pair.
 */
export const turnArtifactItems = (
  group: ClientEventGroup,
): Array<Conversation.AllToolOutputItems<string>> =>
  group.timeline.flatMap((ng) => ng.operations.flatMap((op) => op.result?.payload.items ?? []));

/**
 * @desc What a single turn's gallery needs to know about the rest of the conversation (ADR-21).
 *
 * Cross-turn lineage is shown, not enforced: an older turn still renders and downloads the
 * artefact it delivered, and merely says that a newer revision exists further down. That is a
 * marker, so it is an index built once over every group — never a second fold, which would let
 * a later turn silently rewrite what an earlier one delivered.
 */
export type ArtifactLineageIndex = {
  /** @desc Highest revision seen anywhere in the conversation, per `lineageId`. */
  latestRevisionByLineage: ReadonlyMap<string, number>;
  /** @desc `<name>.<format>` per artefact id, for resolving a `derivedFrom` pointer. */
  labelByArtifactId: ReadonlyMap<string, string>;
  /**
   * @desc What the transcript itself knows about one artefact, per artefact id.
   *
   * A file card rendered inside the prose (ADR-22) needs the description, format, revision and
   * size that only the tool-output item carries — and it is created by react-markdown from a
   * link, so no prop reaches it. This is the same fold as the two maps above, reading two more
   * fields off items already being walked, rather than a second pass or a per-card request.
   */
  metaByArtifactId: ReadonlyMap<string, ArtifactRefMeta>;
};

/** @desc The transcript's own answer for one artefact, as a file card renders it. */
export type ArtifactRefMeta = {
  /** @desc `<name>.<format>` — what a download saves as. */
  label: string;
  name: string;
  description: string;
  format: string;
  revision: number;
  /** @desc The artefact this one was built from, unresolved — provenance, never lineage. */
  derivedFrom: string | null;
  /** @desc Bytes, where the producing tool reported them (images, pdf and xlsx do; csv does not — its summary reports rows/columns instead). */
  fileSize?: number;
};

/**
 * @desc The byte size an output item reports, when its summary carries one.
 *
 * The bitmap, pdf and xlsx summaries declare `fileSize`; this reads structurally rather than
 * switching on format, so a summary that grows the field later is picked up without a change
 * here, and one that never has it stays silent rather than showing a wrong number.
 */
export const itemFileSize = (summary: unknown): number | undefined => {
  const size = (summary as { fileSize?: unknown } | null)?.fileSize;
  return typeof size === 'number' ? size : undefined;
};

export const artifactLineageIndex = (groups: readonly ClientEventGroup[]): ArtifactLineageIndex => {
  const latestRevisionByLineage = new Map<string, number>();
  const labelByArtifactId = new Map<string, string>();
  const metaByArtifactId = new Map<string, ArtifactRefMeta>();

  for (const group of groups) {
    for (const item of turnArtifactItems(group)) {
      if (item.artifactId === null || !item.artifact) continue;
      const { name, description, lineageId, revision, derivedFrom } = item.artifact;

      const label = `${name}.${item.format}`;
      labelByArtifactId.set(item.artifactId, label);
      const fileSize = itemFileSize(item.summary);
      metaByArtifactId.set(item.artifactId, {
        label,
        name,
        description,
        format: item.format,
        revision,
        derivedFrom,
        ...(fileSize !== undefined ? { fileSize } : {}),
      });
      if (revision > (latestRevisionByLineage.get(lineageId) ?? 0)) {
        latestRevisionByLineage.set(lineageId, revision);
      }
    }
  }

  return { latestRevisionByLineage, labelByArtifactId, metaByArtifactId };
};

export function collectEventGroups(
  agg: ClientEventGroup[],
  evt: Conversation.ClientEvent,
): ClientEventGroup[] {
  // Find existing group or create new one
  let group = agg.find((g) => g.turnId === evt.metadata.turnId);
  const wasFound = !!group;

  if (!group) {
    // Create new group with default values
    group = {
      turnId: evt.metadata.turnId,
      tStart: evt.metadata.turn?.tStart,
      prompt: '',
      timeline: [],
      response: null,
      isThinking: false,
      isWarmingUp: false,
      error: null,
      lengthAdvisory: false,
    };
  }

  // Preserve first tStart if it exists
  if (!group.tStart && evt.metadata.turn?.tStart) {
    group.tStart = evt.metadata.turn.tStart;
  }

  // Process event based on type
  if (evt.name === 'ClaudeMessage') {
    // Normalize content to array format early (handle string case)
    const normalizedContent = Array.isArray(evt.payload.content)
      ? evt.payload.content
      : [{ type: 'text' as const, text: evt.payload.content }];

    if (evt.source === 'user') {
      // User message - extract prompt text
      const textContent = normalizedContent
        .filter((c): c is ClaudeMessage.C.Text => c.type === 'text')
        .map((c) => c.text)
        .join('');
      group.prompt = textContent; // Always update to latest user message
    } else if (evt.source === 'model') {
      // Model started responding - no longer thinking
      group.isThinking = false;

      // Assistant message - process content blocks
      const textBlocks: ClaudeMessage.C.Text[] = [];
      const toolUseBlocks: ClaudeMessage.C.ToolUse[] = [];
      const thinkingParts: string[] = [];

      for (const content of normalizedContent) {
        if (content.type === 'text') {
          textBlocks.push(content);
        } else if (content.type === 'tool_use') {
          toolUseBlocks.push(content);
        } else if (content.type === 'thinking') {
          // Summarised reasoning block (present when thinking display='summarized')
          const { thinking } = content as { thinking?: string };
          if (thinking) thinkingParts.push(thinking);
        }
      }

      // Interleaved reasoning that preceded this model call. tFirstChunk is the
      // time-to-first-text, which approximates the thinking duration since text
      // only begins streaming once thinking completes.
      const stepStart = evt.metadata.modelCall?.tStart;

      const thinkingText = thinkingParts.join('');
      const thinking: ThinkingTrace | undefined = thinkingText
        ? {
            text: thinkingText,
            tElapsed: evt.metadata.modelCall?.tFirstChunk ?? evt.metadata.modelCall?.tLastChunk,
          }
        : undefined;

      // Handle text content
      if (textBlocks.length > 0) {
        const combinedText = textBlocks.map((b) => b.text).join('');

        // If there are tool use blocks mixed with text, treat the text as narration with operations
        if (toolUseBlocks.length > 0) {
          // Create a narration group with the text and its associated operations
          const narrationGroup: NarrationGroup = {
            thinking,
            narration: {
              text: combinedText,
              timestamp: evt.timestamp,
            },
            operations: toolUseBlocks.map((toolUse) => ({
              call: toolUse,
              result: null,
              stepStart,
            })),
          };
          group.timeline.push(narrationGroup);
        } else {
          // No tools, this is a regular response
          // If we already have a response, convert it to a narration without operations
          if (group.response) {
            group.timeline.push({
              narration: {
                text: group.response.text,
                timestamp: group.response.timestamp,
              },
              operations: [],
            });
          }

          // Reasoning that preceded the final answer renders just above it as its
          // own timeline entry (no narration/operations of its own).
          if (thinking) {
            group.timeline.push({
              thinking,
              narration: { text: '', timestamp: evt.timestamp },
              operations: [],
            });
          }

          // Set the new response
          group.response = {
            text: combinedText,
            timestamp: evt.timestamp,
            // Legacy documents may lack `id` at runtime despite the type (see `TEvent.id`).
            messageId: (evt.id as string | undefined) || undefined,
          };
        }
      } else if (toolUseBlocks.length > 0) {
        // Tool use blocks without text - create narration group without narration text
        // This handles cases where tools are called without accompanying narration
        const narrationGroup: NarrationGroup = {
          thinking,
          narration: {
            text: '',
            timestamp: evt.timestamp,
          },
          operations: toolUseBlocks.map((toolUse) => ({
            call: toolUse,
            result: null,
            stepStart,
          })),
        };
        group.timeline.push(narrationGroup);
      } else if (thinking) {
        // Reasoning-only message (no text, no tools) — surface it on its own.
        group.timeline.push({
          thinking,
          narration: { text: '', timestamp: evt.timestamp },
          operations: [],
        });
      }
    }
  } else if (evt.name === 'ToolOutput') {
    // Match tool output to its corresponding tool use in the timeline
    for (const narrationGroup of group.timeline) {
      const operation = narrationGroup.operations.find(
        (op) => op.call.id === evt.payload.toolUseId,
      );
      if (operation) {
        operation.result = evt;
        break;
      }
    }
    // If no matching tool use found, we ignore the orphaned output
  } else if (evt.name === 'System') {
    // Handle thinking start event
    if (evt.payload.code === 'ThinkingStart') {
      group.isThinking = true;
    }
    // Handle warming up event
    if (evt.payload.code === 'WarmingUp') {
      group.isWarmingUp = true;
    }
    // The conversation has grown long enough to be worth restarting. Guidance, not a failure —
    // it deliberately does not touch `error`, which drives the turn-failed presentation.
    if (evt.payload.code === 'ConversationLengthAdvisory') {
      group.lengthAdvisory = true;
    }
    // A model call that ended the turn with nothing to show. The persisted event is the single
    // source of truth for this: the group stops claiming the agent is working, and the failure
    // renders where the answer would have been — on the live stream and on every later reload,
    // from the same reduction.
    //
    // The message is NEVER `payload.error`: that is internal detail (a provider account message,
    // a stack, a connection string) and this value is rendered in the conversation. Only the
    // `reason` is read, and everything without one is reported generically.
    if (evt.payload.code === 'ModelCallFailed') {
      group.isThinking = false;
      group.isWarmingUp = false;
      group.error =
        evt.payload.reason === 'output_truncated'
          ? agentStreamError(AgentStreamErrorKind.Truncated, TRUNCATED_OUTPUT_MESSAGE)
          : agentStreamError(AgentStreamErrorKind.Internal, '');
      if (evt.payload.kind === 'system-failure') {
        group.systemFailure = {
          message: evt.payload.message?.trim() ?? '',
          reference: evt.payload.reference ?? evt.payload.requestId ?? null,
        };
      }
    }
    // The harness's notice of a turn that ended without an answer (harness-kit 2.2). The sentence
    // shown is ours, by `reason` — the kit's names tools and model internals — and the notice never
    // replaces an outcome the group already has:
    // - `output_truncated` always follows its `ModelCallFailed`, which already set the truncation
    //   outcome above, so it is skipped rather than shown twice;
    // - `repair_budget_exhausted` (a tool break) and `watchdog` have no other persisted outcome, so
    //   without this the turn ended on its failed tool rows and nothing else.
    // One notice per turn, and `??=` keeps a repeated one (same `signature`) from restating it.
    if (evt.payload.code === 'TurnEnded' && evt.payload.reason !== 'output_truncated') {
      group.isThinking = false;
      group.isWarmingUp = false;
      group.error ??= agentStreamError(
        AgentStreamErrorKind.Internal,
        evt.payload.reason === 'repair_budget_exhausted'
          ? TOOL_BREAK_MESSAGE
          : evt.payload.reason === 'watchdog'
            ? TURN_TIMEOUT_MESSAGE
            : '',
      );
    }
    // Other system events don't affect the group structure directly
    // They're mainly for metadata and state tracking
  }

  // Return updated accumulator
  if (wasFound) {
    return agg; // Group was updated in place
  } else {
    return [...agg, group]; // Add new group to accumulator
  }
}

const initState = () => ({
  // list of conversations
  listings: new Array<Conversation.Listing>(),
  // Conversations data - indexed by conversation ID
  contexts: {} as Record<string, Conversation.ClientEvent[]>,
  //
  grouped: {} as Record<string, ClientEventGroup[]>,
  // AgentManager instance for conversation orchestration
  agentManager: null as AgentManager | null,
  // Track active agents by ID (conversationId or 'new')
  activeAgents: new Set<string>(),
  // Selected agent mode (persisted across sessions). Frontend drives the
  // user-facing default — new conversations start in No-fuss.
  selectedAgentMode: AgentMode.Modes.NoFuss as AgentMode.T,
});

type ConversationState = ReturnType<typeof initState>;

const storeMiddleware = persist(immer<ConversationState>(initState), {
  name: 'conversation-store',
  storage: createJSONStorage(() => localStorage, {
    replacer: (key, value) => SuperJSON.stringify(value),
    reviver: (key, value) => SuperJSON.parse(value as string),
  }),
  partialize: (state) =>
    getAppEnv().storage.disableLocalStorageCache
      ? {}
      : {
          // Only persist conversations + mode choice, not agentManager or activeAgents (runtime state)
          contexts: state.contexts,
          selectedAgentMode: state.selectedAgentMode,
        },
});

export const useConversationStore = create<ConversationState>()(
  devtools(storeMiddleware, {
    name: 'conversation-store',
  }),
);

export class ConversationStore {
  static setListings(items: Conversation.Listing[]) {
    useConversationStore.setState((state) => {
      state.listings = items;
    });
  }

  static setConversation(conversation: Conversation.Serial) {
    useConversationStore.setState((state) => {
      const { history, ...item } = conversation;

      // push raw events to history stack
      state.contexts[conversation._id] = history;

      // create grouped view for rendering
      const newGrouped = history.reduce(collectEventGroups, []);
      // console.debug(conversation._id, newGrouped);
      state.grouped[conversation._id] = newGrouped;

      // update listing state
      const idx = state.listings.findIndex((item) => item._id === conversation._id);
      if (idx === -1) state.listings.push(item);
      else state.listings[idx] = item;
    });
  }

  static addHistoryEvent(conversationId: string, event: Conversation.ClientEvent) {
    useConversationStore.setState((state) => {
      if (!(conversationId in state.contexts)) state.contexts[conversationId] = [];
      const history = state.contexts[conversationId]!;

      // Check if event with same ID exists (optimistic update)
      const existingIndex = history.findIndex((e) => e.id === event.id);
      if (existingIndex !== -1) {
        // Replace optimistic event with server event (has real turnId and metadata)
        history[existingIndex] = event;

        // Rebuild grouped view from complete history to propagate turnId update
        state.grouped[conversationId] = history.reduce(collectEventGroups, []);

        return;
      }

      // add event to history stack
      history.push(event);

      // use reducer to append event to existing grouped view, or to generate fresh view from complete history
      const groups = state.grouped[conversationId];
      state.grouped[conversationId] = groups
        ? [event].reduce(collectEventGroups, groups)
        : history.reduce(collectEventGroups, []);

      // update timestamp on listing
      const listing = state.listings.find((item) => item._id === conversationId);
      if (listing) listing.updatedAt = new Date();
      else {
        logger.warn('[ConversationStore] Cannot update listing timestamp, conversation missing', {
          conversationId,
        });
      }
    });
  }

  static updateConversationFromSync(sync: Conversation.Serial) {
    useConversationStore.setState((state) => {
      const { history, ...item } = sync;

      // update listing metadata
      const listingIdx = state.listings.findIndex((item) => item._id === sync._id);
      if (listingIdx !== -1) state.listings[listingIdx] = item;
      else state.listings.push(item);

      // set history if missing
      if (!(sync._id in state.contexts)) state.contexts[sync._id] = history;
    });
  }

  static getContext(conversationId: string) {
    return useConversationStore.getState().contexts[conversationId];
  }

  static getGrouped(conversationId: string) {
    return useConversationStore.getState().grouped[conversationId] ?? [];
  }

  static getAgentManager(config?: AgentManagerConfig): AgentManager {
    let manager = useConversationStore.getState().agentManager;
    if (manager) {
      // console.debug('[ConversationStore] AgentManager already initialized, skipping');
      return manager;
    }

    // create a new manager instance if missing
    manager = new AgentManager(config);
    useConversationStore.setState((state) => {
      state.agentManager = manager;
    });

    // console.debug('[ConversationStore] AgentManager initialized');
    return manager;
  }

  /**
   * @desc Attach a turn-ending failure to the most recent group, so the UI can show why the agent
   * stopped instead of just dropping the spinner.
   *
   * Targets the last group rather than a turnId: a turn refused before it starts has no server
   * turnId at all — the only group is the one built from the optimistic user message, which carries
   * a client-minted id. Appending a fresh event to an existing group preserves this, while the
   * full rebuild on the next prompt clears it, which is the behaviour we want on a retry.
   */
  static setTurnError(conversationId: string, error: AgentStreamError) {
    useConversationStore.setState((state) => {
      const groups = state.grouped[conversationId];
      const group = groups?.[groups.length - 1];
      if (!group) return;
      group.error = error;
      group.isThinking = false;
      group.isWarmingUp = false;
    });
  }

  static addActiveAgent(agentId: string) {
    useConversationStore.setState((state) => {
      state.activeAgents.add(agentId);
    });
  }

  static removeActiveAgent(agentId: string) {
    useConversationStore.setState((state) => {
      state.activeAgents.delete(agentId);
    });
  }

  static isAgentActive(agentId: string): boolean {
    return useConversationStore.getState().activeAgents.has(agentId);
  }

  static getActiveAgents(): Set<string> {
    return new Set(useConversationStore.getState().activeAgents);
  }

  static setSelectedAgentMode(mode: AgentMode.T) {
    useConversationStore.setState((state) => {
      state.selectedAgentMode = mode;
    });
  }

  static getSelectedAgentMode(): AgentMode.T {
    return useConversationStore.getState().selectedAgentMode;
  }

  static updateSummary(conversationId: string, summary: string) {
    useConversationStore.setState((state) => {
      const listing = state.listings.find((item) => item._id === conversationId);
      if (!listing) {
        logger.error('[ConversationStore] Cannot update listing summary, conversation missing', {
          conversationId,
        });
        return;
      }

      listing.summary = summary;
      listing.updatedAt = new Date();
    });
  }
}
